/**
 * The NavLog table.
 *
 * Fixed layout, so a column does not resize while a pilot types into it; the leg
 * number and the two checkpoints are sticky, so a route scrolled sideways never
 * stops saying which leg you are reading (§9.4). Units live in the header — once
 * per column, never per cell.
 */
import { useT } from '../../i18n';
import { NavLogRow } from './NavLogRow';
import { widthOf, type Column } from './columns';
import type { NavRow } from './model';

export function NavLogTable({
  rows, columns, hidden, lph, stale, readOnly, read, caption,
}: {
  rows: readonly NavRow[];
  columns: readonly Column[];
  hidden: readonly Column[];
  lph: number | null;
  /** leg numbers whose wind no longer describes the track they were read for */
  stale: ReadonlySet<number>;
  readOnly: boolean;
  read(id: string): string;
  caption: string;
}) {
  const t = useT();
  return (
    /* the table is as wide as its columns, in px — a fixed layout that guesses
       its own width is a layout that moves while a value is typed */
    <table className="nl-table" style={{ width: widthOf(columns) + 'px' }}>
      <caption className="nl-sr">{caption}</caption>
      <colgroup>
        {columns.map(c => (
          <col key={c.key} style={{ width: c.w + 'px' }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map(c => (
            <th
              key={c.key}
              scope="col"
              className={'nl-h nl-c-' + c.key + (c.text ? ' text' : ' num')}
              {...(c.full ? { title: t(c.full) } : {})}
            >
              {c.key === 'idx' ? (
                <span className="nl-sr">{t(c.head)}</span>
              ) : (
                <>
                  <span className="nl-hlabel">{t(c.head)}</span>
                  {c.unit ? <span className="nl-unit">{c.unit}</span> : null}
                </>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <NavLogRow
            key={r.id}
            row={r}
            columns={columns}
            hidden={hidden}
            lph={lph}
            windStale={r.kind === 'leg' && typeof r.row === 'number' && stale.has(r.row)}
            readOnly={readOnly}
            read={read}
          />
        ))}
      </tbody>
    </table>
  );
}
